import { Controller } from "@hotwired/stimulus"

// Draws the digits of one real one-time-code input into a row of cells and
// submits the form once the code is complete. See Ui::CodeInputComponent.
export default class extends Controller {
  static targets = ["input", "cell", "error"]

  connect() {
    this.element.classList.add("code-input--enhanced")
    this.render()
    if (this.inputTarget.autofocus) this.inputTarget.focus()
  }

  // Autofill and paste can bring spaces or dashes ("123 456"), so keep digits
  // only instead of capping the input with maxlength, which would cut the
  // pasted text before it is cleaned.
  input() {
    const digits = this.inputTarget.value.replace(/\D/g, "").slice(0, this.cellTargets.length)
    if (digits !== this.inputTarget.value) this.inputTarget.value = digits
    this.clearError()
    this.render()

    if (digits.length === this.cellTargets.length) this.submit()
  }

  focus() {
    this.moveCaretToEnd()
    this.render()
  }

  // The caret is hidden, so keep it at the end: typing always appends and
  // Backspace always removes the last digit.
  moveCaretToEnd() {
    const end = this.inputTarget.value.length
    requestAnimationFrame(() => this.inputTarget.setSelectionRange(end, end))
  }

  // The server marks a wrong code as an error. Typing starts a new attempt, so
  // drop the error text and the aria link to it then; on focus alone the reader
  // may not have seen it yet.
  clearError() {
    if (!this.hasErrorTarget) return

    this.element.classList.remove("code-input--invalid")
    this.inputTarget.removeAttribute("aria-invalid")
    this.inputTarget.removeAttribute("aria-describedby")
    this.errorTarget.remove()
  }

  render() {
    const value = this.inputTarget.value
    const focused = document.activeElement === this.inputTarget
    const activeIndex = Math.min(value.length, this.cellTargets.length - 1)

    this.cellTargets.forEach((cell, index) => {
      cell.textContent = value[index] || ""
      const active = focused && index === activeIndex
      cell.classList.toggle("code-input__cell--active", active)
      cell.classList.toggle("code-input__cell--caret", active && !value[index])
    })
  }

  submit() {
    const form = this.inputTarget.form
    if (!form || form.getAttribute("aria-busy") === "true") return

    // requestSubmit() so Turbo handles the submission (see CLAUDE.md).
    form.requestSubmit()
  }
}
